import { AuthStatus, AuthType, init, logout } from '@thoughtspot/visual-embed-sdk'
import { clearSentryUser, setSentryUser } from '../sentry/helper'
import { createContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { logError, logMessage } from '../error/logger'
import * as Constants from '../../constants'
import { getCdnUrl } from '../cdnAssets/helper'
import { sendDelightedSurvey } from '../delighted/helper.ts'
import { MangoSplash } from '@intellihr/blueberry'

interface IAuthContextType {
  isAuthenticated: boolean
  isVisibleLoginFailedAlert: boolean
  isLoginAuthProcessing: boolean
  isReloadAuthProcessing: boolean
  login: (username: string, password: string) => void
  logoutFromSystem: () => void
  orgId: number | null
  hasExistingSession?: boolean
}

interface ILocationOptions {
  state: {
    from: {
      pathname: string
    }
  }
}

const tsHost = import.meta.env.VITE_THOUGHTSPOT_HOST
const tsPreauthInfoUrl = `${tsHost}/prism/preauth/info`

export const AuthContext = createContext<IAuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [isVisibleLoginFailedAlert, setIsVisibleLoginFailedAlert] = useState<boolean>(false)
  const [isLoginAuthProcessing, setIsLoginAuthProcessing] = useState<boolean>(false)
  const [isReloadAuthProcessing, setIsReloadAuthProcessing] = useState<boolean>(true)
  const [hasExistingSession, setHasExistingSession] = useState<boolean>(false)
  const [orgId, setOrgId] = useState<null | number>(null)
  const navigate = useNavigate()
  const location: ILocationOptions = useLocation()
  const previousPath = location.state?.from.pathname
  const hasLoggedOut = useRef(false)
  const iconSpriteUrl = getCdnUrl(Constants.TS_ICON_SPRITE_PATH)

  useEffect(() => {
    const loginWithExistingSession = (currentOrgId: number, userName: string) => {
      {
        hasLoggedOut.current = false
        // Initialize the SDK with the existing session
        // We are using the None authType because the session is already authenticated and since we are relying on TS session
        // we don't have the implementation to handle the user auth
        const authStatus = init({
          thoughtSpotHost: tsHost,
          authType: AuthType.None,
          username: userName,
          loginFailedMessage: 'Login failed. Please try again.',
          customizations: {
            content: {
              strings: Constants.TS_STRING_REPLACEMENTS,
            },
            style: Constants.TS_CUSTOM_STYLE_BLOCK,
            iconSpriteUrl,
          },
        })

        authStatus.on(AuthStatus.SDK_SUCCESS, () => {
          setIsAuthenticated(true)
          setIsVisibleLoginFailedAlert(false)
          setOrgId(currentOrgId)
          setHasExistingSession(true)
          setSentryUser(userName)
          sendDelightedSurvey({
            email: undefined,
            name: userName,
          })
        })

        authStatus.on(AuthStatus.FAILURE, () => {
          setIsAuthenticated(false)
          setHasExistingSession(false)
          setIsVisibleLoginFailedAlert(true)
          setOrgId(null)
        })
      }
    }
    setIsReloadAuthProcessing(true)
    const fetchPreauthInfo = async () => {
      try {
        const response = await fetch(tsPreauthInfoUrl, {
          method: 'GET',
          credentials: 'include',
        })
        const data = (await response.json()) as Record<string, unknown>
        // preauth info is only available if the user has an existing session
        if (Object.keys(data).includes('info')) {
          const userName = (data.info as { userName: string }).userName
          const currentOrgId = (data.info as { currentOrgId: number }).currentOrgId
          if (userName.length > 0) {
            loginWithExistingSession(currentOrgId, userName)
          }
        }
      } catch {
        logMessage(`Failed to fetch preauth info`)
      }
    }

    fetchPreauthInfo()
      .catch(() => {
        logMessage(`Failed to fetch preauth info`)
        setIsReloadAuthProcessing(false)
      })
      .finally(() => {
        setIsReloadAuthProcessing(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const login = useCallback(
    (username: string, password: string) => {
      try {
        setIsLoginAuthProcessing(true)
        setHasExistingSession(false)
        hasLoggedOut.current = false
        if (username && password) {
          const authStatus = init({
            thoughtSpotHost: tsHost,
            authType: AuthType.Basic,
            username,
            password,
            loginFailedMessage: 'Login failed. Please try again.',
            customizations: {
              content: {
                strings: Constants.TS_STRING_REPLACEMENTS,
              },
              style: Constants.TS_CUSTOM_STYLE_BLOCK,
              iconSpriteUrl,
            },
          })

          authStatus
            .on(AuthStatus.SDK_SUCCESS, () => {
              setIsAuthenticated(true)
              setIsVisibleLoginFailedAlert(false)
              navigate(previousPath && previousPath !== '/login' && previousPath !== '/logout' ? previousPath : '/')
              setSentryUser(username)
              sendDelightedSurvey({
                email: undefined,
                name: username,
              })
            })
            .once(AuthStatus.SUCCESS, (sessionInfo: { currentOrgId: number } | null) => {
              setOrgId(sessionInfo?.currentOrgId ?? null)
            })
          authStatus.on(AuthStatus.FAILURE, () => {
            setIsAuthenticated(false)
            setIsVisibleLoginFailedAlert(true)
            setOrgId(null)
          })
        }
      } catch {
        logMessage('Failed to login using basic authentication')
      } finally {
        setIsLoginAuthProcessing(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated, isVisibleLoginFailedAlert, isLoginAuthProcessing, previousPath],
  )

  const logoutFromSystem = useCallback(() => {
    // When the App is in StrictMode, the logoutFromSystem function is called twice
    // This is a workaround to prevent the second call from executing
    if (hasLoggedOut.current) {
      return
    }
    hasLoggedOut.current = true
    setIsReloadAuthProcessing(true)
    setIsLoginAuthProcessing(true)
    logout()
      .then(() => {
        setIsAuthenticated(false)
        setIsVisibleLoginFailedAlert(false)
        setIsReloadAuthProcessing(false)
        setIsLoginAuthProcessing(false)
        setOrgId(null)
        setHasExistingSession(false)
        clearSentryUser()
      })
      .catch((e) => {
        if (e instanceof Error) {
          logError(e)
        } else {
          logMessage('Error: Caught unknown object when logging out')
        }
      })
  }, [])

  const contextValue: IAuthContextType = useMemo(() => {
    return {
      isAuthenticated,
      isVisibleLoginFailedAlert,
      isLoginAuthProcessing,
      login,
      logoutFromSystem,
      orgId,
      hasExistingSession,
      isReloadAuthProcessing,
    }
  }, [
    isAuthenticated,
    isVisibleLoginFailedAlert,
    isLoginAuthProcessing,
    login,
    logoutFromSystem,
    orgId,
    hasExistingSession,
    isReloadAuthProcessing,
  ])

  if (isReloadAuthProcessing || isLoginAuthProcessing) {
    return <MangoSplash />
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export type { IAuthContextType }
